import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CentinelaAvatar } from "../../../components/base/CentinelaChat";
import { AudioPlayer, Badge, EmptyState } from "../../../components/ui";
import { useCentinelaCallRecording } from "../../../features/centinela/queries";
import { dayKeyOf, type CentinelaCallView } from "../services/mapCentinelaCall";
import { blockWaveform, waveformAllowed } from "../services/recordingWaveform";
import { CENTINELA_CALL_OUTCOME_TONES, CENTINELA_CALL_TYPE_ICONS } from "../theme";

export interface ConversationPanelProps {
  calls: CentinelaCallView[];
  operatorName: string;
}

export default function ConversationPanel({ calls, operatorName }: ConversationPanelProps) {
  const { t, i18n } = useTranslation("common");
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const [audioCallId, setAudioCallId] = useState<string | null>(null);
  const [noWaveform, setNoWaveform] = useState(!waveformAllowed());

  const renewedFor = useRef<string | null>(null);

  const recordingQuery = useCentinelaCallRecording(audioCallId);
  const recording = recordingQuery.data?.recording ?? null;
  const audioUrl = recording?.disponible ? recording.url : null;
  const audioUnavailable = Boolean(recording && !recording.disponible);

  useEffect(() => {
    setNoWaveform(!waveformAllowed());
  }, [audioCallId]);

  const dropWaveform = useCallback(() => {
    blockWaveform();
    setNoWaveform(true);
  }, []);

  const renewAudio = () => {
    if (!audioCallId || renewedFor.current === audioCallId) return;
    renewedFor.current = audioCallId;
    void recordingQuery.refetch();
  };

  const playAudio = (callId: string) => {
    if (audioCallId === callId) {
      renewedFor.current = null;
      void recordingQuery.refetch();
      return;
    }
    renewedFor.current = null;
    setAudioCallId(callId);
  };

  const time = (iso: string) =>
    new Date(iso).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });

  const duration = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${`${seconds % 60}`.padStart(2, "0")}`;

  const dayLabel = (iso: string) => {
    const now = new Date();
    const day = dayKeyOf(iso);
    if (day === dayKeyOf(now.toISOString())) return t("monitoreoIa.calls.today");
    if (day === dayKeyOf(new Date(now.getTime() - 86400000).toISOString()))
      return t("monitoreoIa.calls.yesterday");
    const date = new Date(iso);
    return date.toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "long",
      ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    });
  };

  if (calls.length === 0) {
    return (
      <EmptyState
        icon="ri-phone-line"
        title={t("monitoreoIa.calls.empty")}
        description={t("monitoreoIa.calls.emptyDescription", { name: operatorName })}
      />
    );
  }

  const openCall = calls.find((call) => call.id === openCallId);

  if (openCall) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => {
            setOpenCallId(null);
            setAudioCallId(null);
          }}
          className="flex items-center gap-2 rounded-full px-2 py-1 text-xs text-content-secondary transition-colors hover:bg-subtle hover:text-content"
        >
          <i className="ri-arrow-left-line" />
          {t("monitoreoIa.calls.backToHistory")}
        </button>

        <div className="rounded-xl border border-line bg-surface p-3.5">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500/10">
              <i className={`${CENTINELA_CALL_TYPE_ICONS[openCall.callType] ?? "ri-phone-line"} text-sm text-primary-500`} />
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-bold text-content">
                  {t(`monitoreoIa.centinelaCalls.types.${openCall.callType}`, { defaultValue: openCall.callType })}
                </span>
                <Badge tone={CENTINELA_CALL_OUTCOME_TONES[openCall.outcome]} size="sm">
                  {t(`monitoreoIa.centinelaCalls.outcomes.${openCall.outcome}`)}
                </Badge>
                {openCall.hasRecording && (
                  <Badge tone="neutral" size="sm">
                    <i className="ri-mic-line text-[9px] max-sm:text-[10px]" />
                    {t("monitoreoIa.calls.recorded")}
                  </Badge>
                )}
                {openCall.duressDetected && (
                  <Badge tone="error" size="sm">
                    <i className="ri-alarm-warning-line text-[9px] max-sm:text-[10px]" />
                    {t("monitoreoIa.centinelaCalls.outcomes.duress")}
                  </Badge>
                )}
              </div>

              {openCall.findings.length > 0 && (
                <ul className="mt-1.5 space-y-1">
                  {openCall.findings.map((finding, index) => (
                    <li
                      key={`${openCall.id}-f${index}`}
                      className="rounded-lg border border-line bg-card px-2.5 py-1.5"
                    >
                      <span className="block text-[10px] max-sm:text-[10px] font-semibold uppercase tracking-wide text-content-faint">
                        {t(`monitoreoIa.centinelaCalls.findings.${finding.tipo}`, {
                          defaultValue: finding.tipo,
                        })}
                      </span>
                      {finding.nota && (
                        <span className="mt-0.5 block text-[11px] leading-snug text-content-secondary">
                          {finding.nota}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] max-sm:text-[11px] text-content-faint">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-[9px] max-sm:text-[10px]" />
                  {dayLabel(openCall.startedAt)} · {time(openCall.startedAt)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-record-circle-line text-[9px] max-sm:text-[10px]" />
                  {duration(openCall.durationSeconds)}
                </span>
                <span>
                  {t("monitoreoIa.calls.messageCount", { count: openCall.transcript.length })}
                </span>
              </div>
            </div>
          </div>
        </div>

        <ul className="space-y-3">
          {openCall.transcript.map((message) => {
            const isSentinel = message.isSentinel;

            return (
              <li
                key={message.id}
                className={`flex gap-2.5 ${isSentinel ? "" : "flex-row-reverse"}`}
              >
                {isSentinel ? (
                  <CentinelaAvatar size="sm" className="mt-0.5" />
                ) : (
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-info/15">
                    <i className="ri-user-3-line text-[10px] text-info" />
                  </span>
                )}

                <div className={`min-w-0 flex-1 ${isSentinel ? "" : "flex flex-col items-end"}`}>
                  <span className="mb-0.5 flex items-center gap-1.5">
                    <span
                      className={`text-[10px] max-sm:text-[11px] font-semibold ${
                        isSentinel ? "text-primary-500" : "text-info"
                      }`}
                    >
                      {message.speaker ??
                        (isSentinel ? t("monitoreoIa.assistantName") : operatorName)}
                    </span>
                    <span className="text-[9.5px] max-sm:text-[10px] text-content-faint">{time(message.at)}</span>
                  </span>

                  <p
                    className={`max-w-[90%] rounded-xl px-3 py-2 text-[11.5px] leading-relaxed ${
                      isSentinel
                        ? "rounded-tl-none border border-line bg-surface text-content"
                        : "rounded-tr-none bg-subtle text-content-secondary"
                    }`}
                  >
                    {message.text}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="space-y-2 rounded-xl border border-line bg-surface p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-[11px] text-content-secondary">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full ${
                  openCall.hasRecording ? "bg-primary-500/15" : "bg-subtle"
                }`}
              >
                <i
                  className={`text-[10px] max-sm:text-[11px] ${
                    openCall.hasRecording
                      ? "ri-mic-line text-primary-500"
                      : "ri-mic-off-line text-content-faint"
                  }`}
                />
              </span>
              {openCall.hasRecording
                ? t("monitoreoIa.calls.audioAvailable")
                : t("monitoreoIa.calls.audioUnavailable")}
            </span>

            <span className="flex items-center gap-2 text-[11px] text-content-faint">
              {duration(openCall.durationSeconds)}
              {openCall.hasRecording && !audioUrl && (
                <button
                  type="button"
                  disabled={recordingQuery.isFetching}
                  onClick={() => playAudio(openCall.id)}
                  title={t("monitoreoIa.calls.audioPlay")}
                  aria-label={t("monitoreoIa.calls.audioPlay")}
                  className="flex h-9 w-9 sm:h-7 sm:w-7 items-center justify-center rounded-full border border-line bg-card text-primary-500 transition-colors hover:bg-subtle disabled:opacity-60"
                >
                  <i
                    className={`text-xs ${
                      recordingQuery.isFetching ? "ri-loader-4-line animate-spin" : "ri-play-fill"
                    }`}
                  />
                </button>
              )}
            </span>
          </div>

          {audioUrl ? (
            <AudioPlayer
              autoPlay
              size="md"
              src={audioUrl}
              duration={openCall.durationSeconds}
              waveform={!noWaveform}
              playLabel={t("monitoreoIa.calls.audioPlay")}
              pauseLabel={t("monitoreoIa.calls.audioPause")}
              seekLabel={t("monitoreoIa.calls.audioSeek")}
              muteLabel={t("monitoreoIa.calls.audioMute")}
              unmuteLabel={t("monitoreoIa.calls.audioUnmute")}
              volumeLabel={t("monitoreoIa.calls.audioVolume")}
              onError={renewAudio}
              onWaveformBlocked={dropWaveform}
            />
          ) : null}

          {recordingQuery.isError ? (
            <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-error">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-xs" />
              {t("monitoreoIa.calls.audioFailed")}
            </p>
          ) : null}

          {audioUnavailable ? (
            <p className="text-[11px] leading-relaxed text-content-faint">
              {t("monitoreoIa.calls.audioMissing")}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {calls.map((call, index) => (
        <li key={call.id}>
          {(index === 0 || dayKeyOf(calls[index - 1].startedAt) !== dayKeyOf(call.startedAt)) && (
            <div className="flex items-center gap-2 py-2">
              <span className="h-px flex-1 bg-line" />
              <span className="rounded-full bg-subtle px-2.5 py-0.5 text-[10px] max-sm:text-[11px] font-medium text-content-faint">
                {dayLabel(call.startedAt)}
              </span>
              <span className="h-px flex-1 bg-line" />
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              setOpenCallId(call.id);
              setAudioCallId(null);
            }}
            className="group flex w-full items-start gap-3 rounded-xl border border-line bg-surface p-3.5 text-left transition-colors hover:border-primary-500/40"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-500/10">
              <i className={`${CENTINELA_CALL_TYPE_ICONS[call.callType] ?? "ri-phone-line"} text-sm text-primary-500`} />
            </span>

            <span className="min-w-0 flex-1">
              <span className="mb-0.5 flex flex-wrap items-center gap-2">
                <span className="truncate text-[12.5px] font-bold text-content">
                  {t(`monitoreoIa.centinelaCalls.types.${call.callType}`, { defaultValue: call.callType })}
                </span>
                <Badge tone={CENTINELA_CALL_OUTCOME_TONES[call.outcome]} size="sm">
                  {t(`monitoreoIa.centinelaCalls.outcomes.${call.outcome}`)}
                </Badge>
              </span>

              <span className="line-clamp-2 block text-[11.5px] leading-snug text-content-secondary">
                {call.findings[0]?.nota ?? call.transcript[0]?.text}
              </span>

              <span className="mt-2 flex flex-wrap items-center gap-3 text-[10px] max-sm:text-[11px] text-content-faint">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-[9px] max-sm:text-[10px]" />
                  {time(call.startedAt)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-record-circle-line text-[9px] max-sm:text-[10px]" />
                  {duration(call.durationSeconds)}
                </span>
                <span>
                  {t("monitoreoIa.calls.messageCount", { count: call.transcript.length })}
                </span>
              </span>
            </span>

            <i className="ri-arrow-right-s-line mt-1 shrink-0 text-content-faint transition-colors group-hover:text-primary-500" />
          </button>
        </li>
      ))}
    </ul>
  );
}
